import { Component, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Platform } from '@app/classes';
import { BulkSelectEmployeeDialogComponent } from '@app/components';
import { FeatureFlag } from '@app/enums';
import { AnalyticEvents } from '@app/enums/analytic-events.enum';
import { RoleAssignment } from '@app/models/account/role-assignment.model';
import { SettingsRoleMenuService } from '@app/modules/settings/services';
import { MenuColumnEvent } from '@app/modules/table/interfaces/menu-column-event.interface';
import { AnalyticService } from '@app/services';
import { AuthService } from '@app/services/auth.service';
import { FeatureService } from '@app/services/feature.service';
import { NotifyService } from '@app/services/notify.service';
import { Translatable } from '@app/types/translatable.type';
import { RoleNameDisplayOptions } from '@app/types/translatables/role-display.options';
import { IndexView } from '@common/views/index/index.view';
import { Role } from '@models/account/role.model';
import { Employee } from '@models/employee/employee.model';
import { RoleEmployeesTable } from '@settings/services/tables/role-employees-table/role-employees.table';
import { SettingsResolver } from '@settings/settings.resolver';

// This function was added to fix SQA-2092. This function will filter employees in employeesToUnassign list
// based on whether they are currently assigned to the role.
const filterEmployeesToUnassign = (employeesToAssign: Employee[], employeesToUnassign: Employee[]): Employee[] => {
    if (!employeesToAssign.length) {
        return employeesToUnassign;
    }

    return employeesToUnassign.filter((unassignedEmployee: Employee) => {
        return employeesToAssign.some((assignedEmployee: Employee) => {
            return assignedEmployee.id !== unassignedEmployee.id;
        });
    });
};

@Component({
    templateUrl: './employee-index.template.html',
})
export class EmployeeIndexView extends IndexView {
    @ViewChild('appEmployeeSelection', { static: true }) appEmployeeSelection!: BulkSelectEmployeeDialogComponent;

    isLoading = false;
    role: Role;
    isEmpty = true;
    table!: RoleEmployeesTable;
    roleNameDisplay = RoleNameDisplayOptions;
    hasKYCKYBColumn = false;

    constructor(
        private auth: AuthService,
        private route: ActivatedRoute,
        private router: Router,
        private notify: NotifyService,
        private analytic: AnalyticService,
        private resolver: SettingsResolver,
        private featureService: FeatureService,
        public readonly settingsRoleMenuService: SettingsRoleMenuService
    ) {
        super();
        this.role = this.route.snapshot.data.models.get('role');
        this.role.permissions;

        this.featureService.has(FeatureFlag.KYC_KYB).then((hasKYCKYBFeatureFlag) => {
            this.hasKYCKYBColumn =
                this.auth.company.isPayrollSyncEnabled &&
                this.auth.companyHasModule(Platform.modules.payroll) &&
                hasKYCKYBFeatureFlag &&
                this.role.permissions.some((permission) => permission.name === 'accessPayroll');
            this.reloadTable();
        });
    }

    get roleName(): Translatable {
        return RoleNameDisplayOptions[this.role.name] ?? this.role.name;
    }

    reloadTable(): void {
        this.table = new RoleEmployeesTable(this.auth, this.role, this.hasKYCKYBColumn);
    }

    onTableMenuEvent(event: MenuColumnEvent<Employee, string>): void {
        if (event.menuItem.event !== 'revoke') {
            return;
        }

        const employee = event.data;
        const roleAssignment = this.role.roleAssignments.find(
            (assignment) => assignment.accountId === employee?.accountId
        );

        if (!roleAssignment) {
            this.notify.error('settings.role-employees.employeeNotAssignedErrorMessage');
            return;
        }

        this.onUnassign(roleAssignment);
    }

    async onUnassign(roleAssignment: RoleAssignment): Promise<void> {
        if (roleAssignment.isOnlyRoleAssignmentInCompany) {
            this.notify.error('settings.role-employees.employeeMinimumRolesErrorMessage');
            return;
        }

        this.isLoading = true;

        try {
            await roleAssignment.param('account', roleAssignment.accountId).delete();
            this.notify.success('settings.role-employees.roleRevokedSuccessMessage');
            const route = await this.resolver.resolve(this.route.snapshot, this.router.routerState.snapshot);
            this.role = <Role>route.get('role');

            this.reloadTable();
        } catch (err) {
            this.notify.error(err);
        } finally {
            this.isLoading = false;
        }
    }

    async onChangeAssignments(): Promise<void> {
        const previouslyAssignedEmployees = this.role.roleAssignments
            .map((roleAssignment) => roleAssignment.account?.employee)
            .filter((employee) => employee !== null && employee !== undefined);

        const selectedEmployees = await this.appEmployeeSelection.show<Employee[] | null>([
            ...previouslyAssignedEmployees,
        ]);

        const userCanceledModal = selectedEmployees === null;

        if (userCanceledModal) {
            return;
        }

        const employeesToAssign = selectedEmployees.filter(
            (selectedEmployee) => !previouslyAssignedEmployees.includes(selectedEmployee)
        );

        const employeesToUnassign = previouslyAssignedEmployees.filter((previouslyAssignedEmployee: Employee) => {
            return !selectedEmployees.some((selectedEmployee) => selectedEmployee.id === previouslyAssignedEmployee.id);
        });

        const assignmentsHaveChanges = employeesToAssign.length || employeesToUnassign.length;

        if (!assignmentsHaveChanges) {
            return;
        }

        this.persistAssignmentChanges(employeesToAssign, employeesToUnassign);
    }

    private async persistAssignmentChanges(employeesToAssign: Employee[], employeesToUnassign: Employee[]) {
        employeesToUnassign = filterEmployeesToUnassign(employeesToAssign, employeesToUnassign);

        const assignments = employeesToAssign.map(async (employee) => {
            const roleAssignment = new RoleAssignment({ roleId: this.role.id });
            return await roleAssignment.param('account', employee.accountId).save();
        });

        const unAssignments = employeesToUnassign
            .map((employee) =>
                this.role.roleAssignments.find((roleAssignment) => roleAssignment.account?.employee?.is(employee))
            )
            .filter((roleAssignment) => !roleAssignment?.isOnlyRoleAssignmentInCompany)
            .map(async (roleAssignment) => await roleAssignment?.param('account', roleAssignment.accountId).delete());

        try {
            this.isLoading = true;
            await Promise.all([...unAssignments, ...assignments]);
            this.analytic.trackEvent(AnalyticEvents.AssignRole, {
                roleId: this.role.id,
                role: this.role.name,
            });
            this.notify.success('settings.role-employees.roleAssignmentSuccessMessage');

            const route = await this.resolver.resolve(this.route.snapshot, this.router.routerState.snapshot);
            this.role = <Role>route.get('role');
            this.isLoading = false;

            this.reloadTable();
        } catch (err) {
            this.notify.error(err);
            this.isLoading = false;
        }
    }
}
